import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  ScrollText,
  RefreshCw,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  User,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { auditApi } from "@/lib/crmApi";
import { formatDateTime } from "@/lib/crmHelpers";

/**
 * v2.7.0 — Journal d'activité (admin only).
 *
 * Affiche les événements horodatés stockés dans `db.audit_log` côté backend.
 * Filtres : type d'événement, user, projet, période. Pagination skip/limit.
 *
 * Liste des events surveillés actuellement :
 *   - lead_status_changed     (drag-drop Kanban + édition manuelle)
 *   - lead_priority_changed
 *   - lead_assigned           (réassignation manuelle ou auto)
 *   - lead_deleted
 *   - bulk_send               (envoi groupé WhatsApp)
 *   - sequence_created / sequence_updated / sequence_deleted
 *   - project_deleted         (legacy v1.6.x)
 *
 * Cette liste grandira au fur et à mesure qu'on auditera plus d'actions —
 * le backend renvoie `audit-log/events` (distinct) pour peupler le filtre.
 */

// Labels FR pour les events. Si un event manque ici, on fallback sur la
// valeur brute (lisible mais en snake_case).
const EVENT_LABEL = {
  lead_status_changed: "Statut lead modifié",
  lead_priority_changed: "Priorité lead modifiée",
  lead_assigned: "Lead réassigné",
  lead_deleted: "Lead supprimé",
  bulk_send: "Envoi groupé WhatsApp",
  sequence_created: "Séquence créée",
  sequence_updated: "Séquence modifiée",
  sequence_deleted: "Séquence supprimée",
  project_deleted: "Projet supprimé",
};

// Tonalité visuelle par criticité — un suppression saute aux yeux, un
// changement de statut reste discret.
const EVENT_TONE = {
  lead_deleted: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  project_deleted: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  sequence_deleted: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  bulk_send: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  lead_assigned: "bg-violet-50 text-violet-700 dark:bg-violet-950/40 dark:text-violet-300",
  sequence_created: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  sequence_updated: "bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  lead_status_changed: "bg-muted text-foreground",
  lead_priority_changed: "bg-muted text-foreground",
};

const PAGE_SIZE = 50;

// Format compact d'un meta pour la colonne "Détails" — on évite de dumper
// tout le JSON, l'admin clique sur la ligne pour le voir si besoin.
function summarizeMeta(event, meta) {
  if (!meta) return "—";
  switch (event) {
    case "lead_status_changed":
      return `${meta.lead_nom || "?"} : ${meta.from || "?"} → ${meta.to || "?"}`;
    case "lead_priority_changed":
      return `${meta.from || "?"} → ${meta.to || "?"}`;
    case "lead_assigned":
      return meta.to_user_nom
        ? `→ ${meta.to_user_nom}`
        : meta.to_user_id
        ? `→ ${meta.to_user_id}`
        : "désassigné";
    case "lead_deleted":
      return `${meta.lead_nom || "?"} (${meta.telephone || "—"})`;
    case "bulk_send":
      return `${meta.sent ?? 0}/${meta.total ?? 0} envoyés${
        meta.failed ? ` · ${meta.failed} échec` : ""
      }`;
    case "sequence_created":
    case "sequence_updated":
    case "sequence_deleted":
      return meta.nom || meta.sequence_id || "—";
    case "project_deleted":
      return `${meta.projet_nom || "?"} (${meta.leads_deleted ?? 0} leads)`;
    default:
      return JSON.stringify(meta).slice(0, 80);
  }
}

function EventChip({ event }) {
  const tone = EVENT_TONE[event] || "bg-muted text-muted-foreground";
  const label = EVENT_LABEL[event] || event;
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`}
      title={event}
    >
      {label}
    </span>
  );
}

function AuditRow({ row, expanded, onToggle }) {
  return (
    <div
      className="border-b border-border/40 px-3 py-2 transition hover:bg-muted/30"
      data-testid={`audit-row-${row.ts}`}
    >
      <button
        onClick={onToggle}
        className="flex w-full items-start gap-3 text-left"
      >
        <span className="flex-none text-[11px] text-muted-foreground tabular-nums">
          {formatDateTime(row.ts)}
        </span>
        <span className="flex-none">
          <EventChip event={row.event} />
        </span>
        <span className="min-w-0 flex-1 truncate text-xs">
          {summarizeMeta(row.event, row.meta)}
        </span>
        <span className="flex-none text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <User className="h-3 w-3" />
            {row.user_nom || row.user_email || "système"}
          </span>
        </span>
        {expanded ? (
          <ChevronUp className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 flex-none text-muted-foreground" />
        )}
      </button>
      {expanded && (
        <pre className="mt-2 max-h-60 overflow-auto rounded bg-muted/60 p-2 text-[10.5px] leading-snug text-foreground">
          {JSON.stringify(row, null, 2)}
        </pre>
      )}
    </div>
  );
}

export default function AuditLogPanel() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  // Filtres
  const [eventFilter, setEventFilter] = useState("all"); // "all" or one event
  const [sinceFilter, setSinceFilter] = useState("");
  const [untilFilter, setUntilFilter] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  // Fetch event types once — drives the filter dropdown.
  useEffect(() => {
    auditApi
      .eventTypes()
      .then((d) => setEventTypes(d.events || []))
      .catch(() => setEventTypes([]));
  }, []);

  const load = useCallback(
    async (resetOffset = false) => {
      setLoading(true);
      setError(null);
      try {
        const params = {
          limit: PAGE_SIZE,
          offset: resetOffset ? 0 : offset,
        };
        if (eventFilter && eventFilter !== "all") params.event = eventFilter;
        if (sinceFilter) params.since = new Date(sinceFilter).toISOString();
        if (untilFilter) params.until = new Date(untilFilter).toISOString();
        const data = await auditApi.list(params);
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
        if (resetOffset) {
          setOffset(0);
          setRows(data.items || []);
        } else {
          setRows((prev) =>
            offset === 0 ? data.items || [] : [...prev, ...(data.items || [])]
          );
        }
      } catch (e) {
        if (e?.response?.status === 403) {
          setError("Réservé à l'administrateur.");
        } else {
          setError(e?.response?.data?.detail || "Impossible de charger le journal.");
        }
      } finally {
        setLoading(false);
      }
    },
    [eventFilter, sinceFilter, untilFilter, offset]
  );

  // Initial load + reload on filter change. We reset offset to 0 every time
  // a filter shifts because the page slice is no longer meaningful.
  useEffect(() => {
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventFilter, sinceFilter, untilFilter]);

  const loadMore = async () => {
    const newOffset = offset + PAGE_SIZE;
    setOffset(newOffset);
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: newOffset };
      if (eventFilter && eventFilter !== "all") params.event = eventFilter;
      if (sinceFilter) params.since = new Date(sinceFilter).toISOString();
      if (untilFilter) params.until = new Date(untilFilter).toISOString();
      const data = await auditApi.list(params);
      setRows((prev) => [...prev, ...(data.items || [])]);
      setHasMore(!!data.has_more);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error("Échec du chargement de la suite");
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setEventFilter("all");
    setSinceFilter("");
    setUntilFilter("");
  };

  const hasFilters =
    (eventFilter && eventFilter !== "all") || !!sinceFilter || !!untilFilter;

  return (
    <div className="rounded-xl border border-border/60 bg-card">
      <div className="flex items-center justify-between border-b border-border/40 px-4 py-3">
        <div className="flex items-center gap-2">
          <ScrollText className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-heading text-sm font-semibold">
            Journal d'activité
          </h3>
          <span className="text-[11px] text-muted-foreground">
            {total} évènement{total > 1 ? "s" : ""}
          </span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => load(true)}
          disabled={loading}
          data-testid="audit-refresh"
          className="h-7 gap-1.5 text-xs"
        >
          <RefreshCw
            className={`h-3 w-3 ${loading ? "animate-spin" : ""}`}
          />
          Rafraîchir
        </Button>
      </div>

      {/* Filtres */}
      <div className="grid gap-3 border-b border-border/40 bg-muted/20 px-4 py-3 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <div className="space-y-1">
          <Label className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
            Type d'évènement
          </Label>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="h-8 text-xs" data-testid="audit-event-filter">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les évènements</SelectItem>
              {eventTypes.map((ev) => (
                <SelectItem key={ev} value={ev}>
                  {EVENT_LABEL[ev] || ev}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
            Depuis
          </Label>
          <Input
            type="datetime-local"
            value={sinceFilter}
            onChange={(e) => setSinceFilter(e.target.value)}
            className="h-8 text-xs"
            data-testid="audit-since"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-[10.5px] uppercase tracking-wide text-muted-foreground">
            Jusqu'à
          </Label>
          <Input
            type="datetime-local"
            value={untilFilter}
            onChange={(e) => setUntilFilter(e.target.value)}
            className="h-8 text-xs"
            data-testid="audit-until"
          />
        </div>
        <div className="flex items-end">
          {hasFilters && (
            <Button
              variant="outline"
              size="sm"
              onClick={clearFilters}
              className="h-8 gap-1 text-xs"
              data-testid="audit-clear-filters"
            >
              <X className="h-3 w-3" />
              Réinitialiser
            </Button>
          )}
        </div>
      </div>

      {/* Body */}
      {error ? (
        <div className="flex items-center gap-2 px-4 py-6 text-sm text-red-600">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      ) : rows.length === 0 && !loading ? (
        <div className="px-4 py-10 text-center text-sm text-muted-foreground">
          <Filter className="mx-auto mb-2 h-5 w-5" />
          Aucun évènement {hasFilters ? "ne correspond à ce filtre" : "encore enregistré"}.
        </div>
      ) : (
        <div className="scrollbar-thin max-h-[600px] overflow-y-auto">
          {rows.map((row, idx) => {
            const rid = `${row.ts}-${idx}`;
            return (
              <AuditRow
                key={rid}
                row={row}
                expanded={expandedId === rid}
                onToggle={() =>
                  setExpandedId((prev) => (prev === rid ? null : rid))
                }
              />
            );
          })}
          {hasMore && (
            <div className="border-t border-border/40 px-4 py-3 text-center">
              <Button
                variant="outline"
                size="sm"
                onClick={loadMore}
                disabled={loading}
                data-testid="audit-load-more"
                className="text-xs"
              >
                {loading ? "Chargement…" : `Charger ${PAGE_SIZE} de plus`}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
